import { useEffect, useMemo, useState } from "react";
import { PageLabel } from "../components/ui/PageLabel";

type StyleId = "flat" | "stacked" | "chrome" | "extruded" | "gold" | "neon" | "outline" | "glitch";

const STYLES: Array<{ id: StyleId; label: string; desc: string }> = [
  { id: "flat",     label: "1 · Flat",     desc: "Single fill — baseline." },
  { id: "stacked",  label: "2 · Stacked",  desc: "N copies offset & darkened — fake extrusion." },
  { id: "chrome",   label: "3 · Chrome",   desc: "Silver gradient + bevel filter + outline." },
  { id: "extruded", label: "4 · Extruded", desc: "Stack + chrome face — closest to comic-book look." },
  { id: "gold",     label: "5 · Gold",     desc: "Same chrome treatment, warm bronze→gold gradient." },
  { id: "neon",     label: "6 · Neon",     desc: "Multi-radius glow halo. White core, color glow." },
  { id: "outline",  label: "7 · Outline",  desc: "Hollow stencil. fill=none + thick rounded stroke." },
  { id: "glitch",   label: "8 · Glitch",   desc: "Cyan + magenta channels offset behind main fill." },
];

const FONTS = ["Russo One", "Anton", "Bebas Neue", "Black Ops One", "Bungee"] as const;

const CHROME_STOPS = `
  <stop offset="0%"  stop-color="#3a3a3a"/>
  <stop offset="22%" stop-color="#dddddd"/>
  <stop offset="46%" stop-color="#ffffff"/>
  <stop offset="50%" stop-color="#f4f4f4"/>
  <stop offset="54%" stop-color="#ffffff"/>
  <stop offset="78%" stop-color="#7a7a7a"/>
  <stop offset="100%" stop-color="#1a1a1a"/>`;
const GOLD_STOPS = `
  <stop offset="0%"  stop-color="#4a2400"/>
  <stop offset="22%" stop-color="#d49b3a"/>
  <stop offset="46%" stop-color="#ffe07a"/>
  <stop offset="50%" stop-color="#fff3b8"/>
  <stop offset="54%" stop-color="#ffe07a"/>
  <stop offset="78%" stop-color="#a06a18"/>
  <stop offset="100%" stop-color="#2a1300"/>`;

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function darkenHex(hex: string, amount: number): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const t = 1 - Math.max(0, Math.min(1, amount));
  const r = Math.round(((n >> 16) & 0xff) * t);
  const g = Math.round(((n >> 8) & 0xff) * t);
  const b = Math.round((n & 0xff) * t);
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

function bevelFilter(id: string): string {
  return `<filter id="${id}" x="-10%" y="-10%" width="120%" height="120%">
    <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" result="blur"/>
    <feSpecularLighting in="blur" surfaceScale="4" specularConstant="1.4" specularExponent="22" lighting-color="white" result="spec">
      <feDistantLight azimuth="225" elevation="55"/>
    </feSpecularLighting>
    <feComposite in="spec" in2="SourceAlpha" operator="in" result="specCut"/>
    <feMerge>
      <feMergeNode in="SourceGraphic"/>
      <feMergeNode in="specCut"/>
    </feMerge>
  </filter>`;
}

interface Params {
  word: string;
  font: string;
  color: string;
  skew: number;
  depth: number;
  stroke: number;
}

function buildBody(style: StyleId, p: Params): { defs: string; body: string } {
  const { word, font, depth, stroke, color } = p;
  const fs = 280;
  const baseAttrs = `text-anchor="middle" font-family="'${font}', sans-serif" font-size="${fs}" font-weight="900"`;
  const escaped = escapeXml(word);
  const uid = style;

  if (style === "flat") {
    return { defs: "", body: `<text x="0" y="0" ${baseAttrs} fill="${color}">${escaped}</text>` };
  }
  if (style === "stacked") {
    const layers: string[] = [];
    for (let i = depth; i >= 1; i--) {
      const t = i / depth;
      const shade = darkenHex(color, 0.4 + 0.45 * t);
      layers.push(`<text x="${i * 0.7}" y="${i * 0.7}" ${baseAttrs} fill="${shade}">${escaped}</text>`);
    }
    layers.push(`<text x="0" y="0" ${baseAttrs} fill="${color}">${escaped}</text>`);
    return { defs: "", body: layers.join("") };
  }
  if (style === "outline") {
    const sw = Math.max(1, stroke);
    return { defs: "", body: `<text x="0" y="0" ${baseAttrs} fill="none" stroke="${color}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round">${escaped}</text>` };
  }
  if (style === "neon") {
    const fid = `lp-neon-${uid}`;
    const defs = `<filter id="${fid}" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b1"/>
      <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b2"/>
      <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="b3"/>
      <feMerge>
        <feMergeNode in="b3"/><feMergeNode in="b2"/><feMergeNode in="b1"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>`;
    const sw = Math.max(1, Math.round(fs * 0.035));
    return { defs, body: `<g filter="url(#${fid})"><text x="0" y="0" ${baseAttrs} fill="#ffffff" stroke="${color}" stroke-width="${sw}" paint-order="stroke fill">${escaped}</text></g>` };
  }
  if (style === "glitch") {
    const off = Math.max(2, Math.round(fs * 0.04));
    return {
      defs: "",
      body: [
        `<text x="${-off}" y="0" ${baseAttrs} fill="#00e7ff" opacity="0.85">${escaped}</text>`,
        `<text x="${off}"  y="0" ${baseAttrs} fill="#ff2bd6" opacity="0.85">${escaped}</text>`,
        `<text x="0" y="0" ${baseAttrs} fill="${color}">${escaped}</text>`,
      ].join(""),
    };
  }
  // chrome / gold / extruded
  const isGold = style === "gold";
  const gid = `lp-grad-${uid}`;
  const fid = `lp-bevel-${uid}`;
  const defs = `<linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1">${isGold ? GOLD_STOPS : CHROME_STOPS}</linearGradient>${bevelFilter(fid)}`;
  const sw = Math.max(0, stroke);
  const strokeAttr = sw > 0 ? ` stroke="${color}" stroke-width="${sw}" paint-order="stroke fill"` : "";
  const face = `<text x="0" y="0" ${baseAttrs} fill="url(#${gid})"${strokeAttr} filter="url(#${fid})">${escaped}</text>`;
  if (style === "chrome" || style === "gold") return { defs, body: face };
  // extruded
  const layers: string[] = [];
  for (let i = depth; i >= 1; i--) {
    const t = i / depth;
    const shade = darkenHex(color, 0.55 + 0.35 * t);
    layers.push(`<text x="${i * 0.7}" y="${i * 0.7}" ${baseAttrs} fill="${shade}">${escaped}</text>`);
  }
  layers.push(face);
  return { defs, body: layers.join("") };
}

function renderStyle(style: StyleId, p: Params): string {
  const { defs, body } = buildBody(style, p);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 700">
    ${defs ? `<defs>${defs}</defs>` : ""}
    <g transform="translate(800 380)${p.skew !== 0 ? ` skewX(${p.skew})` : ""}">
      ${body}
    </g>
  </svg>`;
}

const FONT_LINK_HREF =
  "https://fonts.googleapis.com/css2?family=Russo+One&family=Black+Ops+One&family=Bungee&family=Anton&family=Bebas+Neue&display=swap";

function ensureFontsLoaded() {
  if (typeof document === "undefined") return;
  if (document.querySelector('link[data-threed-fonts="1"]')) return;
  const pre1 = document.createElement("link");
  pre1.rel = "preconnect";
  pre1.href = "https://fonts.googleapis.com";
  pre1.dataset.threedFonts = "1";
  const pre2 = document.createElement("link");
  pre2.rel = "preconnect";
  pre2.href = "https://fonts.gstatic.com";
  pre2.crossOrigin = "";
  pre2.dataset.threedFonts = "1";
  const css = document.createElement("link");
  css.rel = "stylesheet";
  css.href = FONT_LINK_HREF;
  css.dataset.threedFonts = "1";
  document.head.appendChild(pre1);
  document.head.appendChild(pre2);
  document.head.appendChild(css);
}

export function ThreeDLogoPage() {
  const [word, setWord] = useState("LOGOMAN");
  const [font, setFont] = useState<(typeof FONTS)[number]>("Russo One");
  const [color, setColor] = useState("#e63946");
  const [skew, setSkew] = useState(-10);
  const [depth, setDepth] = useState(10);
  const [stroke, setStroke] = useState(6);

  useEffect(() => {
    ensureFontsLoaded();
  }, []);

  const params: Params = { word: word || "LOGOMAN", font, color, skew, depth, stroke };

  const panels = useMemo(
    () => STYLES.map((s) => ({ ...s, svg: renderStyle(s.id, params) })),
    [params.word, params.font, params.color, params.skew, params.depth, params.stroke],
  );

  return (
    <div className="page threed-logo">
      <PageLabel name="3D LOGO" />
      <header className="threed-header">
        <h1>3D Text Style Sketch v2</h1>
        <p className="lede">8 styles. Pure SVG. Adjust controls to test before committing.</p>
      </header>
      <div className="threed-controls">
        <label>
          <span>Word</span>
          <input type="text" value={word} onChange={(e) => setWord(e.target.value)} />
        </label>
        <label>
          <span>Font</span>
          <select value={font} onChange={(e) => setFont(e.target.value as (typeof FONTS)[number])}>
            {FONTS.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Title color</span>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
        </label>
        <label>
          <span>Skew (deg)</span>
          <input
            type="range"
            min={-20}
            max={0}
            value={skew}
            onChange={(e) => setSkew(parseInt(e.target.value, 10))}
          />
        </label>
        <label>
          <span>Depth</span>
          <input
            type="range"
            min={1}
            max={30}
            value={depth}
            onChange={(e) => setDepth(parseInt(e.target.value, 10))}
          />
        </label>
        <label>
          <span>Outline</span>
          <input
            type="range"
            min={0}
            max={20}
            value={stroke}
            onChange={(e) => setStroke(parseInt(e.target.value, 10))}
          />
        </label>
      </div>
      <div className="threed-grid">
        {panels.map((p) => (
          <div key={p.id} className="threed-panel">
            <h2>{p.label}</h2>
            <div className="threed-desc">{p.desc}</div>
            <div className="threed-stage" dangerouslySetInnerHTML={{ __html: p.svg }} />
          </div>
        ))}
      </div>
    </div>
  );
}
